// External Imports
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';

// Internal Imports
import { logger } from '../../utils/rollbar.js';

// See https://support.speedcurve.com/docs/single-page-applications#recommended-implementation-for-a-spa for extra information
function SpeedcurveRum({ history, location }) {
  const [hasMarkedLoadTime, setHasMarkedLoadTime] = useState(false);
  const stringifiedLocation = JSON.stringify(location);
  const [isSpeedcurveLoaded, setIsSpeedcurveLoaded] = useState(false);

  useEffect(() => {
    globalThis?.LUX?.mark('magazineAppLoaded');
    const speedcurve = document.querySelector('#speedcurve');
    if (!speedcurve) {
      logger.error('The expected Speedcurve script with id #speedcurve was not found on the page!');
      return;
    }
    const handleSpeedcurveLoading = () => setIsSpeedcurveLoaded(true);
    const handleSpeedcurveError = () =>
      logger.warning(
        'Speedcurve script failed to load, hence RUM data will not be collected for this user session.',
      );

    speedcurve.addEventListener('load', handleSpeedcurveLoading);
    speedcurve.addEventListener('error', handleSpeedcurveError);
    // eslint-disable-next-line consistent-return -- optional return
    return () => {
      speedcurve.removeEventListener('load', handleSpeedcurveLoading);
      speedcurve.removeEventListener('error', handleSpeedcurveError);
    };
  }, []);

  useEffect(() => {
    if (!isSpeedcurveLoaded) {
      return;
    }
    // Mark the page was fully rendered

    // We are waiting for the speedcurve script to be loaded, so markLoadTime should always be available, but we decided to be conversative here and keep accessing it conditionally
    const markLoadTime = globalThis?.LUX?.markLoadTime;
    if (typeof markLoadTime === 'function') {
      markLoadTime();
      setHasMarkedLoadTime(true);
    } else {
      logger.warning(
        'Speedcurve LUX markLoadTime was not a function, current page load cannot be tracked.',
      );
    }
  }, [stringifiedLocation, isSpeedcurveLoaded]);

  useEffect(() => {
    if (!hasMarkedLoadTime) {
      return () => {};
    }

    const unlisten = history.listen(() => {
      /*
        Send information about previous mark
        the documentation recommends doing this as late as possible to collect as much info as possible
        "Call LUX.send() as late in the page lifecycle as possible to maximize data collection.
        This can even be called directly before LUX.init()"
        NB: this is also done when the page is closed automatically by LUX, so we don't have to manually
        take care of that scenario.
      */

      globalThis?.LUX?.send();
      // Init the next page load

      globalThis?.LUX?.init();
    });
    return unlisten;
  }, [hasMarkedLoadTime, history]);

  // eslint-disable-next-line unicorn/no-null -- React expects null for empty renders
  return null;
}

SpeedcurveRum.propTypes = {
  history: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- this should be a TypeScript type eventually
  location: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- this should be a TypeScript type eventually
};

const SpeedcurveRumHoc = withRouter(SpeedcurveRum);

// Module Exports
export { SpeedcurveRumHoc as SpeedcurveRum };
